/**
 * Single source of truth for the submission fields that must NOT sit on the class
 * document. Kept in its OWN file with ZERO imports so the firebase-admin netlify
 * functions can import it without dragging in React/type transitive deps — same
 * discipline as studentPiiFields.ts, for the same reason.
 *
 * ── WHY THIS FILE EXISTS ──────────────────────────────────────────────────────
 * `ClassData.submissions[]` is embedded on the class doc, and the class doc is
 * readable by every class member (firestore.rules: `request.auth.uid in
 * resource.data.classMembers`). So every field of every submission has been
 * readable by all ~30 students in the class, through the Firebase SDK that ships
 * in the bundle — including three fields whose own type comments claim otherwise:
 *
 *   studentComment  "private note from the student to the teacher (not shown to
 *                    the class)"
 *   thread          "Private back-and-forth between the teacher and THIS student
 *                    about their submission. Not class-visible. The only
 *                    messaging surface in the app."
 *   selfAssessment  a child's own expected grade and reflection
 *
 * The comments described the UI, not a control. Worse than a passive hole: the
 * student portal keeps a live `onSnapshot` on the class doc, so every write by
 * anyone re-pushed every classmate's private conversation into every open student
 * tab. The data was already in their browser.
 *
 * ── WHAT IS AND IS NOT IN HERE ────────────────────────────────────────────────
 * PRIMARY-ONLY fields only: readable by the submitting student and the teacher,
 * never by a group partner. `submissionGroup.ts` and `student-class-write.ts`
 * both already enforce that boundary for threads deliberately, so the split here
 * matches a rule the app already keeps.
 *
 * Deliberately NOT in here yet, and each for a reason:
 *
 *   grade / feedback / gradedAt — group partners legitimately read these on a
 *     shared submission, so they need a second, wider reader set. More
 *     importantly, moving `submission.grade` alone would be theatre: the same
 *     numbers are ALSO in `ClassData.grades` and `ClassData.releasedGrades`,
 *     embedded maps on the same classmate-readable doc. Marks get fixed as one
 *     piece or not at all.
 *
 *   link / portfolioLinks / fileUrl / filePath / fileName / fileType / files —
 *     also group-readable, and `fileUrl` is a Storage DOWNLOAD-TOKEN url, which
 *     works like a "anyone with the link" share and bypasses storage.rules
 *     entirely. Moving the field closes the leak for future readers but does not
 *     revoke a token a classmate already copied, so that change has to come with
 *     token regeneration.
 *
 * Keep this list minimal and move fields deliberately. Every entry here is a
 * field that some reader must be taught to hydrate, and a field left embedded is
 * a field still being broadcast.
 */

/**
 * Submission fields routed to `classes/{id}/submissionPrivate/{submissionId}`,
 * readable only by the submitting student's auth uids and the teacher arms.
 */
export const SUBMISSION_PRIVATE_FIELDS = [
  'studentComment',
  'thread',
  'selfAssessment',
] as const;

export type SubmissionPrivateField = (typeof SUBMISSION_PRIVATE_FIELDS)[number];

/**
 * Split one submission row into the part that may stay on the class doc and the
 * part that may not.
 *
 * Returns the private part as `null` when there is nothing private to store, so
 * callers can skip writing an empty doc rather than littering one per submission.
 * Takes and returns loose records because it runs on both the client (ClassData)
 * and the server (Admin SDK plain objects), and must not depend on types.ts.
 */
export function splitSubmissionPrivate<T extends Record<string, unknown>>(
  row: T,
): { publicPart: T; privatePart: Record<string, unknown> | null } {
  const publicPart = { ...row };
  const privatePart: Record<string, unknown> = {};
  let found = false;
  for (const field of SUBMISSION_PRIVATE_FIELDS) {
    if (!(field in publicPart)) continue;
    const value = publicPart[field];
    delete publicPart[field];
    // `undefined` is not storable in Firestore and an empty thread is not worth a
    // document; treat both as absent rather than writing a hollow row.
    if (value === undefined || value === null) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    privatePart[field] = value;
    found = true;
  }
  return { publicPart, privatePart: found ? privatePart : null };
}

/** Does this row still carry anything that belongs in the private store? */
export function hasSubmissionPrivate(row: Record<string, unknown> | null | undefined): boolean {
  if (!row) return false;
  return SUBMISSION_PRIVATE_FIELDS.some(f => {
    const v = row[f];
    if (v === undefined || v === null) return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  });
}

/**
 * Merge a stored private part back onto a submission row, for a reader entitled
 * to it. Never mutates its inputs.
 */
export function mergeSubmissionPrivate<T extends Record<string, unknown>>(
  row: T,
  privatePart: Record<string, unknown> | null | undefined,
): T {
  if (!privatePart) return row;
  const merged = { ...row } as Record<string, unknown>;
  for (const field of SUBMISSION_PRIVATE_FIELDS) {
    if (privatePart[field] !== undefined) merged[field] = privatePart[field];
  }
  return merged as T;
}

/** The shape a thread message needs for merging. Deliberately loose — it carries
 *  author, role and content too, and this module must not import types.ts (see the
 *  header), so only the two fields the merge actually uses are named. */
type Threadish = { id?: unknown; createdAt?: unknown; [key: string]: unknown };

/**
 * Merge thread messages by id, oldest first. Never loses a message.
 *
 * This is the guard for the sharpest failure in the split. Several paths set the
 * teacher's in-memory class from a RAW class doc with no hydration, so a row can
 * carry no thread at all; the teacher then replies and `[...(s.thread || []), msg]`
 * is just `[msg]`. A wholesale write would replace an entire conversation with that
 * one message — silently, and only for classes that happened through those paths.
 *
 * So a thread write can only ever ADD. Deletion is a separate, explicit act
 * (deleteSubmissionThreadMessage), which is the honest division: "merge what I have"
 * and "remove this" are different intents and a single wholesale write conflated
 * them, getting the dangerous one by default.
 *
 * Also used when a duplicate submission is absorbed, so the survivor ends up with
 * the whole conversation rather than whichever half won the dedupe sort.
 */
export function unionThread(
  stored: readonly Threadish[] | undefined,
  incoming: readonly Threadish[] | undefined,
): Threadish[] {
  const byId = new Map<string, Threadish>();
  for (const list of [stored || [], incoming || []]) {
    for (const m of list) {
      /**
       * A message with no id still has to be kept — dropping one loses a child's
       * words — but it must be keyed on something STABLE, not on a counter.
       *
       * A counter made every id-less message unique to its position, so stored and
       * incoming copies of the SAME message never matched: one save turned one
       * message into two, the next into four, doubling until the document hit
       * Firestore's 1 MB limit. Every writer sets an id today, so this is latent —
       * but "latent exponential growth" is not a property to leave in, and the test
       * that asserted the old behaviour was pinning the bug rather than the safety.
       *
       * Falling back to content+time is exactly what de-duplication needs: two
       * copies of one message collapse, two genuinely different messages do not.
       */
      const hasId = m && m.id !== undefined && m.id !== null && String(m.id) !== '';
      const key = hasId
        ? `id:${String(m.id)}`
        : `noid:${String(m?.createdAt ?? '')}:${String((m as { content?: unknown })?.content ?? '')}`;
      byId.set(key, m);
    }
  }
  return Array.from(byId.values()).sort(
    (a, b) => String(a?.createdAt ?? '').localeCompare(String(b?.createdAt ?? '')),
  );
}

/** Remove one message from a thread by id. The only operation that shrinks one. */
export function pruneThread(
  thread: readonly Threadish[] | undefined,
  messageId: string,
): Threadish[] {
  return (thread || []).filter(m => String(m?.id) !== String(messageId));
}
